import { axios } from '@vyce/core/src/api';

import {
  DRCEvent,
  DRCIR35Payload,
  DRCPeriodPayload,
  DRCProfilePayload,
  DRCReport,
  DRCReportExportPayload,
  DRCReportPayload,
  DRCResponse,
  DRCUserRow,
  DRCUsersSearchPayload,
  END_USER_STATUSES,
  PresignedFile,
} from '../../types';
import config from '../config';

const URL = `${config.gatewayApiUrl}`;

/**
 * VAT Domestic Reverse Charge. Legend only — every route below is mounted on admin/v1.
 *
 * `actor_id` is written by the gateway from the authenticated token and is not declared on
 * any request schema, so it is never sent from here: a client that sends one has it dropped
 * by validation. That is the whole point of the audit trail.
 */

/**
 * The DRC panel: the stored profile and whether the charge applies today. A worker nobody has
 * set up answers 200 with `profile: null` — that is a state, not an error.
 */
export const getDRCProfileRequest = (userId: string, onDate?: string) => {
  return axios.get<DRCResponse>(`${URL}/admin/v1/drc/users/${userId}`, {
    params: onDate ? { on_date: onDate } : undefined,
  });
};

/**
 * Saves the VAT and CIS half of the panel, creating the profile if there is none. Every write
 * is partial — send only what was edited. Changing `vat_number` clears the HMRC verification,
 * because a verification vouches for one number.
 */
export const updateDRCProfileRequest = (userId: string, payload: DRCProfilePayload) => {
  return axios.put<DRCResponse>(`${URL}/admin/v1/drc/users/${userId}`, payload);
};

/** The ruling the whole feature hangs on. Any value but `not_end_user` clears the stamp. */
export const setDRCEndUserStatusRequest = (
  userId: string,
  payload: { status: END_USER_STATUSES; note?: string }
) => {
  return axios.post<DRCResponse>(`${URL}/admin/v1/drc/users/${userId}/end-user`, payload);
};

/** Records a CEST outcome. Never an input to whether the reverse charge applies. */
export const recordDRCIR35Request = (userId: string, payload: DRCIR35Payload) => {
  return axios.post<DRCResponse>(`${URL}/admin/v1/drc/users/${userId}/ir35`, payload);
};

/**
 * Sets the window the charge applies in; both ends inclusive. A date earlier than today is a
 * 422 and `ended_on` before `effective_from` is a 409, so the picker disables past dates
 * rather than letting the server refuse.
 */
export const setDRCPeriodRequest = (userId: string, payload: DRCPeriodPayload) => {
  return axios.post<DRCResponse>(`${URL}/admin/v1/drc/users/${userId}/period`, payload);
};

/**
 * Looks the company up at Companies House and stores the snapshot. Uses `company_number` from
 * the body if given, else the one already on the profile. 404 = no such number, 503 = Companies
 * House unreachable and nothing was written, 422 = no number anywhere.
 */
export const verifyDRCCompanyRequest = (userId: string, companyNumber?: string) => {
  return axios.post<DRCResponse>(
    `${URL}/admin/v1/drc/users/${userId}/company/verify`,
    companyNumber ? { company_number: companyNumber } : {}
  );
};

/** Audit history, newest first. A worker with no profile answers an empty page, not a 404. */
export const getDRCEventsRequest = (userId: string, { offset = 0, limit = 25 }) => {
  return axios.get<{ total: number; items: DRCEvent[] }>(`${URL}/admin/v1/drc/users/${userId}/events`, {
    params: { offset, limit },
  });
};

/**
 * The worker list behind the Legend screen. Every filter is optional and they AND together.
 * `applies` is not a filter — it is derived per row from `on_date`, so filtering by it would
 * break pagination. Filter on the stored fields and read `status.applies` off each item.
 */
export const searchDRCUsersRequest = (payload: DRCUsersSearchPayload, signal?: AbortSignal) => {
  // Note the DRC routes page with `total`, not the `count` the rest of the gateway uses.
  return axios.post<{ total: number; items: DRCUserRow[] }>(`${URL}/admin/v1/drc/users/search`, payload, {
    signal,
  });
};

/** The period report — the figure V Pay Tech files with HMRC. */
export const getDRCReportRequest = (payload: DRCReportPayload, signal?: AbortSignal) => {
  return axios.post<DRCReport>(`${URL}/admin/v1/pay/reports/drc`, payload, { signal });
};

/**
 * CSV is the per-pay-run-line audit feed; PDF is the same summary the screen shows. Two
 * identical requests share one render — the second answers 429 while the first is in flight.
 */
export const exportDRCReportRequest = (payload: DRCReportExportPayload) => {
  return axios.post<PresignedFile>(`${URL}/admin/v1/pay/reports/drc/export`, payload);
};
